import React, { useEffect, useState } from 'react';

/**
 * Small pill badge that polls /api/admin/migrations/status and turns amber
 * when any migration is pending. Uses REACT_APP_ADMIN_KEY from env.
 * Renders nothing until the first fetch succeeds.
 */
const MigrationsStatusBadge = () => {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY;

  useEffect(() => {
    if (!ADMIN_KEY || !API_URL) return undefined;
    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/admin/migrations/status?admin_key=${encodeURIComponent(ADMIN_KEY)}`
        );
        if (!res.ok) {
          if (!cancelled) setError(true);
          return;
        }
        const json = await res.json();
        if (!cancelled) {
          setStatus(json);
          setError(false);
        }
      } catch {
        if (!cancelled) setError(true);
      }
    };

    fetchStatus();
    const id = setInterval(fetchStatus, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [API_URL, ADMIN_KEY]);

  if (error || !status) return null;

  const pending = status.pending?.length || 0;
  const orphaned = status.orphaned?.length || 0;
  const hasWarn = pending > 0 || orphaned > 0;

  const classes = hasWarn
    ? 'bg-amber-100 text-amber-800 border-amber-300'
    : 'bg-emerald-50 text-emerald-700 border-emerald-200';

  const label = hasWarn
    ? `${pending > 0 ? `${pending} pending` : ''}${
        pending > 0 && orphaned > 0 ? ' · ' : ''
      }${orphaned > 0 ? `${orphaned} orphaned` : ''}`
    : `${status.total_applied}/${status.total_registered} migrations`;

  return (
    <a
      href="/admin/migrations"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${classes} hover:opacity-80 transition`}
      data-testid="migrations-status-badge"
      title={
        hasWarn
          ? 'Open migrations dashboard to review.'
          : 'All migrations applied. Click to view details.'
      }
    >
      <span
        className={`inline-block w-1.5 h-1.5 rounded-full ${
          hasWarn ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'
        }`}
      />
      {label}
    </a>
  );
};

export default MigrationsStatusBadge;
